import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  useTheme,
  alpha,
  Alert,
  Box,
} from '@mui/material';
import { useAuth } from '../util/auth';
import { sendInviteEmail } from '../utils/socialActions';
import { Captcha } from 'recaptz';
import { useS } from 'use-s-react';
import { store } from '../store';

const InviteFriendModal = ({ open, onClose }) => {
  const theme = useTheme();
  const auth = useAuth();

  // Use global state for modal open state
  const [inviteModalOpen, setInviteModalOpen] = useS(store.inviteModalOpen);

  // Local state for form
  const [email, setEmail] = useS('');
  const [loading, setLoading] = useS(false);
  const [error, setError] = useS('');
  const [success, setSuccess] = useS('');
  const [captchaValid, setCaptchaValid] = useS(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!captchaValid) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      await sendInviteEmail({
        toEmail: email.toLowerCase(),
        fromEmail: auth.user.email,
        fromUserId: auth.user.uid
      });

      setSuccess('Invitation sent successfully!');
      setEmail('');
      setCaptchaValid(false); // Reset CAPTCHA after successful send
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error sending invite:', error);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Invite a Friend
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ borderRadius: 1 }}>
              {success}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Invite your friends to join and track bank bonuses together. They'll receive an email with your referral code.
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label="Friend's Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                disabled={loading}
                placeholder="Enter your friend's email"
                InputProps={{
                  sx: {
                    borderRadius: 1,
                  }
                }}
              />

              {/* CAPTCHA Verification */}
              <Box>
                <Captcha
                  type="mixed"
                  length={5}
                  darkMode={theme.palette.mode === 'dark'}
                  showSuccessAnimation
                  caseSensitive={false}
                  onValidate={(isValid) => {
                    setCaptchaValid(isValid);
                  }}
                  validationRules={{
                    required: true,
                    minLength: 5,
                    maxLength: 5
                  }}
                />
              </Box>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !email || !captchaValid}
          sx={{
            borderRadius: 1,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            },
          }}
        >
          {loading ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteFriendModal; 